import React, { useState, useEffect, useCallback } from 'react';
import { Circle, Check } from 'lucide-react';

/**
 * EncounterStoplight — 4-state visual status indicator for the current
 * encounter that ALSO syncs to History dock + History Slide Panel so a
 * finished encounter stops appearing as an ongoing draft once the provider
 * marks it complete.
 *
 * States (left → right progression, like a traffic light + amber buffer):
 *   🔴 Red     — Input — notes being entered / dictating, no output yet
 *   🟡 Yellow  — Output Ready — generation complete, drafts shown
 *   🟠 Amber   — Copied — clipboard copy of full note + sig fired (one hop from done)
 *   🟢 Green   — Signed/Submitted — explicit "Signed in Genesis" confirmation
 *
 * Status is keyed by encounter history_id (server) OR falls back to
 * draft_id / a timestamp so guest mode still works. Persisted to
 * localStorage so History dock / Slide Panel can pick up the same state
 * across the session without an extra round-trip.
 *
 * Reported May 14, 2026 — replaces having to open the History bar to see
 * what's done; adds a no-timer 4-state visual cue so providers can pace
 * themselves without anxiety.
 */

const STORAGE_KEY = 'writeright_encounter_status_v1';

const STATES = {
  red: {
    label: 'Input',
    desc: 'Dictating or entering notes',
    dotClass: 'bg-rose-500 shadow-[0_0_8px_2px_rgba(244,63,94,0.55)]',
    textClass: 'text-rose-300',
    pillClass: 'border-rose-500/40 bg-rose-500/10 text-rose-300',
  },
  yellow: {
    label: 'Output Ready',
    desc: 'Drafts generated, awaiting review',
    dotClass: 'bg-yellow-400 shadow-[0_0_8px_2px_rgba(250,204,21,0.55)]',
    textClass: 'text-yellow-300',
    pillClass: 'border-yellow-500/40 bg-yellow-500/10 text-yellow-300',
  },
  amber: {
    label: 'Copied',
    desc: 'Copied to clipboard — last step is submitting',
    dotClass: 'bg-amber-500 shadow-[0_0_8px_2px_rgba(245,158,11,0.55)]',
    textClass: 'text-amber-300',
    pillClass: 'border-amber-500/40 bg-amber-500/10 text-amber-300',
  },
  green: {
    label: 'Signed in Genesis',
    desc: 'Note submitted to Genesis — encounter complete',
    dotClass: 'bg-emerald-500 shadow-[0_0_8px_2px_rgba(16,185,129,0.55)]',
    textClass: 'text-emerald-300',
    pillClass: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
  },
};

// ── localStorage status map helpers (encounter_id → entry) ────────────
//
// Feb 16, 2026: upgraded from `{ [id]: 'green' }` (flat string) to
// `{ [id]: { state, signed_at?, copied_at? } }` to give audit timestamps.
// Old plain-string entries are auto-upgraded on read, so no migration needed.
const _readMap = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') || {}; }
  catch { return {}; }
};

const _writeMap = (map) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(map)); } catch { /* ignore */ }
};

/** Normalize legacy plain-string entries to { state } objects. */
const _readEntry = (encounterId) => {
  if (!encounterId) return null;
  const raw = _readMap()[encounterId];
  if (!raw) return null;
  if (typeof raw === 'string') return { state: raw };
  return raw;
};

export const getEncounterStatus = (encounterId) => {
  const entry = _readEntry(encounterId);
  return entry?.state || 'red';
};

/** Returns the full entry — { state, signed_at?, copied_at? } — for audit display. */
export const getEncounterStatusEntry = (encounterId) => _readEntry(encounterId);

export const setEncounterStatus = (encounterId, state, extras = {}) => {
  if (!encounterId) return;
  const map = _readMap();
  const prev = map[encounterId];
  const prevEntry = (prev && typeof prev === 'object') ? prev : (prev ? { state: prev } : {});
  // Stamp timestamps automatically based on state transitions.
  //
  // Feb 22, 2026 (bug fix): Previously this used `!prevEntry.signed_at` so
  // signed_at was only stamped on the FIRST green transition — meaning if a
  // note was signed yesterday and re-signed today (e.g., after reload from
  // history), `signed_at` kept yesterday's date and the "signed today"
  // counter in the header never bumped. Now: every green transition stamps
  // today's timestamp. The audit log captures the most recent sign action,
  // which is what providers actually care about for the daily streak chip.
  const stamps = {};
  if (state === 'green') stamps.signed_at = new Date().toISOString();
  if (state !== 'green') stamps.signed_at = null; // clear on un-sign
  if (state === 'amber' && !prevEntry.copied_at) stamps.copied_at = new Date().toISOString();
  const entry = { ...prevEntry, state, ...stamps, ...extras };
  // Strip nulls so the JSON stays clean
  Object.keys(entry).forEach((k) => entry[k] === null && delete entry[k]);
  map[encounterId] = entry;
  _writeMap(map);
  try {
    window.dispatchEvent(new CustomEvent('writeright:encounter-status-changed', {
      detail: { encounter_id: encounterId, state, ...entry },
    }));
  } catch { /* ignore */ }
};

/** Friendly relative-time formatter for tooltip display. */
const _formatTimeAgo = (isoString) => {
  if (!isoString) return '';
  try {
    const t = new Date(isoString);
    if (isNaN(t)) return '';
    const diff = Math.max(0, Date.now() - t.getTime());
    const mins = Math.floor(diff / 60000);
    const hours = Math.floor(mins / 60);
    const abs = t.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    if (mins < 1) return `just now (${abs})`;
    if (mins < 60) return `${mins} min ago (${abs})`;
    if (hours < 24) return `${hours} hr ago (${abs})`;
    return abs;
  } catch { return ''; }
};

const EncounterStoplight = ({
  encounterId,
  hasResult,
  notesLength = 0,
  onSignedToggle,   // callback when user clicks "Signed in Genesis"
}) => {
  // Derive a starting state from props if nothing is stored
  const computeAutoState = useCallback(() => {
    const stored = encounterId ? _readEntry(encounterId)?.state : null;
    if (stored) return stored;
    if (hasResult) return 'yellow';
    if ((notesLength || 0) > 0) return 'red';
    return 'red';
  }, [encounterId, hasResult, notesLength]);

  const [state, setState] = useState(computeAutoState);

  // Sync from external events (e.g., copy-all-fired, submitted-to-genesis)
  useEffect(() => {
    const onChange = (e) => {
      if (!encounterId) return;
      if (e.detail?.encounter_id === encounterId) setState(e.detail.state);
    };
    window.addEventListener('writeright:encounter-status-changed', onChange);
    return () => window.removeEventListener('writeright:encounter-status-changed', onChange);
  }, [encounterId]);

  // Auto-progress from red → yellow when generation completes
  useEffect(() => {
    if (hasResult && state === 'red') {
      setState('yellow');
      if (encounterId) setEncounterStatus(encounterId, 'yellow');
    }
  }, [hasResult]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopyAll = (e) => {
    // `e` may be undefined when invoked from the combined "Copy & Sign" button
    // (Feb 24, 2026 fix — previously crashed with "Cannot read properties of
    // undefined (reading 'preventDefault')"). Only prevent default if we
    // actually have a real DOM event.
    if (e && typeof e.preventDefault === 'function') e.preventDefault();
    // Tell the outer panel to fire its own copy-all action
    try {
      window.dispatchEvent(new CustomEvent('writeright:request-copy-all', {
        detail: { encounter_id: encounterId },
      }));
    } catch { /* ignore */ }
    // Promote red→amber, yellow→amber, amber stays amber
    if (state !== 'green') {
      setState('amber');
      if (encounterId) setEncounterStatus(encounterId, 'amber');
    }
  };

  const handleSignedToggle = (e) => {
    // Robust against being invoked programmatically with a synthetic
    // `{ target: { checked: true } }` object (combined Copy & Sign button)
    // — DON'T touch e.preventDefault here; <input type="checkbox"> doesn't
    // need it, and the synthetic shim has no preventDefault function.
    const checked = !!(e && e.target && e.target.checked);
    const next = checked ? 'green' : (hasResult ? 'amber' : 'yellow');
    setState(next);
    if (encounterId) setEncounterStatus(encounterId, next);
    if (onSignedToggle) onSignedToggle(checked);
    try {
      if (checked) {
        window.dispatchEvent(new CustomEvent('writeright:note-submitted', { detail: { encounter_id: encounterId } }));
      }
    } catch { /* ignore */ }
  };

  const cfg = STATES[state] || STATES.red;
  const order = ['red', 'yellow', 'amber', 'green'];
  // Audit timestamp shown on hover when present
  const entry = _readEntry(encounterId);
  const signedAtLabel = entry?.signed_at ? `Signed in Genesis ${_formatTimeAgo(entry.signed_at)}` : '';
  const copiedAtLabel = entry?.copied_at ? `Copied to clipboard ${_formatTimeAgo(entry.copied_at)}` : '';
  const stateTooltip = [cfg.label, signedAtLabel, copiedAtLabel].filter(Boolean).join(' · ');
  const idx = order.indexOf(state);

  // Idle-note warning (Feb 16, 2026 — UX #1). If a note has been copied to
  // clipboard but not signed in Genesis for >20 minutes, warn the provider
  // so they don't accidentally close clinic with unsigned charts. We use
  // copied_at as the anchor (that's the moment the note left the app).
  const [ageMin, setAgeMin] = useState(() => {
    if (!entry?.copied_at) return 0;
    return Math.floor((Date.now() - new Date(entry.copied_at).getTime()) / 60000);
  });
  useEffect(() => {
    if (state !== 'amber' || !entry?.copied_at) {
      setAgeMin(0);
      return;
    }
    const recompute = () => {
      setAgeMin(Math.floor((Date.now() - new Date(entry.copied_at).getTime()) / 60000));
    };
    recompute();
    const id = setInterval(recompute, 60_000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, entry?.copied_at]);
  const idleWarn = state === 'amber' && ageMin >= 20;

  // Combined Copy + Sign action — Feb 16, 2026 UX #2. Fires both stoplight
  // transitions at once so the provider doesn't need two clicks.
  const handleCopyAndSign = () => {
    // Reuse the existing handlers' synthetic events
    handleCopyAll();
    handleSignedToggle({ target: { checked: true } });
  };

  return (
    <div
      className={`rounded-lg border px-3 py-2 transition-colors ${
        idleWarn
          ? 'border-amber-500/60 bg-amber-500/8 animate-pulse'
          : 'border-border/60 bg-card/40'
      }`}
      data-testid="encounter-stoplight"
    >
      {idleWarn && (
        <div className="text-[11px] text-amber-300 font-semibold mb-1.5 flex items-center gap-1.5" data-testid="stoplight-idle-warning">
          <Check className="w-3 h-3 hidden" />
          <span>Pending sign-off · {ageMin} min since copy. Finish in Genesis before clinic ends.</span>
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        {/* Stoplight dots */}
        <div className="flex items-center gap-2" data-testid="stoplight-dots">
          {order.map((s, i) => {
            const isActive = i <= idx;
            const dot = isActive ? STATES[s].dotClass : 'bg-secondary/40';
            return (
              <div key={s} className="flex items-center gap-1.5">
                <span
                  className={`inline-block w-3 h-3 rounded-full transition-all ${dot}`}
                  title={STATES[s].label}
                  data-testid={`stoplight-dot-${s}`}
                />
                {i < order.length - 1 && (
                  <span className={`inline-block w-2 h-px ${isActive && i < idx ? 'bg-foreground/40' : 'bg-border'}`} />
                )}
              </div>
            );
          })}
          <span
            className={`ml-2 text-xs font-semibold ${cfg.textClass}`}
            title={stateTooltip}
            data-testid="stoplight-state-label"
          >
            {cfg.label}
          </span>
          <span className="text-[10px] text-muted-foreground hidden sm:inline" title={stateTooltip}>
            · {cfg.desc}
            {entry?.signed_at && state === 'green' && (
              <span className="ml-1 text-emerald-400/70">· {_formatTimeAgo(entry.signed_at)}</span>
            )}
          </span>
        </div>

        {/* Two-checkbox row */}
        <div className="flex items-center gap-3">
          <label
            className="flex items-center gap-1.5 text-xs cursor-pointer text-foreground/80 hover:text-foreground transition-colors"
            data-testid="stoplight-copy-all-label"
          >
            <input
              type="checkbox"
              checked={state === 'amber' || state === 'green'}
              onChange={handleCopyAll}
              className="w-3.5 h-3.5 accent-amber-500"
              data-testid="stoplight-copy-all"
              disabled={!hasResult}
            />
            <span>Copy all (Note + Sig)</span>
          </label>
          <label
            className="flex items-center gap-1.5 text-xs cursor-pointer text-foreground/80 hover:text-foreground transition-colors"
            data-testid="stoplight-signed-label"
          >
            <input
              type="checkbox"
              checked={state === 'green'}
              onChange={handleSignedToggle}
              className="w-3.5 h-3.5 accent-emerald-500"
              data-testid="stoplight-signed"
              disabled={!hasResult}
            />
            <span>Signed in Genesis</span>
          </label>
          {/* Combo action — one click to do both at once (Feb 16, 2026 UX #2) */}
          {hasResult && state !== 'green' && (
            <button
              type="button"
              onClick={handleCopyAndSign}
              className="px-2 py-1 rounded-md text-[10px] font-bold bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 transition-colors inline-flex items-center gap-1"
              data-testid="stoplight-copy-and-sign"
              title="Copy to clipboard AND mark as signed in Genesis in one click"
            >
              <Check className="w-2.5 h-2.5" />
              Copy &amp; Sign
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Small inline pill version for History dock / Slide Panel rows
export const StoplightPill = ({ encounterId, fallbackComplete = false, fallbackSubmitted = false }) => {
  const computeState = React.useCallback(() => {
    const stored = _readEntry(encounterId)?.state;
    if (stored) return stored;
    if (fallbackSubmitted) return 'green';
    if (fallbackComplete) return 'amber';
    return 'yellow';
  }, [encounterId, fallbackComplete, fallbackSubmitted]);

  const [state, setLocalState] = React.useState(computeState);
  const [entry, setEntry] = React.useState(() => _readEntry(encounterId));

  // Reactive: rerender whenever this encounter's stoplight status flips
  // (e.g., user marks "Submitted to Genesis" from the History dock or slide panel).
  React.useEffect(() => {
    setLocalState(computeState());
    setEntry(_readEntry(encounterId));
    const onChange = (e) => {
      if (!encounterId) return;
      if (e.detail?.encounter_id === encounterId && e.detail?.state) {
        setLocalState(e.detail.state);
        setEntry(_readEntry(encounterId));
      }
    };
    window.addEventListener('writeright:encounter-status-changed', onChange);
    return () => window.removeEventListener('writeright:encounter-status-changed', onChange);
  }, [encounterId, computeState]);

  const cfg = STATES[state] || STATES.yellow;
  const signedAtLabel = entry?.signed_at ? ` · Signed ${_formatTimeAgo(entry.signed_at)}` : '';
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold uppercase tracking-wider border ${cfg.pillClass}`}
      data-testid={`stoplight-pill-${state}`}
      title={`${cfg.desc}${signedAtLabel}`}
    >
      <Circle className={`w-2 h-2 fill-current ${cfg.textClass}`} />
      {cfg.label}
    </span>
  );
};

/**
 * StoplightHeaderIndicator — compact red/yellow/amber/green dot + label
 * for the main toolbar so the provider sees the current encounter state
 * AND can sign with one tap without scrolling back down to the output panel.
 *
 * Reported Feb 22, 2026: "Need red light/green light indicator of completion
 * progress in the main toolbar instead of scrolling up to find checkboxes."
 *
 * Props:
 *   encounterId  — current encounter id (passed from App.js); when null, the
 *                  indicator hides itself (no encounter in progress).
 *   onSignedToggle — same callback shape as EncounterStoplight; used to keep
 *                    backend in sync when the provider signs from the header.
 */
export const StoplightHeaderIndicator = ({ encounterId, onSignedToggle }) => {
  const computeState = React.useCallback(() => {
    return _readEntry(encounterId)?.state || null;
  }, [encounterId]);

  const [state, setLocalState] = React.useState(computeState);
  const [entry, setEntry] = React.useState(() => _readEntry(encounterId));

  React.useEffect(() => {
    setLocalState(computeState());
    setEntry(_readEntry(encounterId));
    const onChange = (e) => {
      if (!encounterId) return;
      if (e.detail?.encounter_id === encounterId) {
        setLocalState(e.detail.state || null);
        setEntry(_readEntry(encounterId));
      }
    };
    window.addEventListener('writeright:encounter-status-changed', onChange);
    return () => window.removeEventListener('writeright:encounter-status-changed', onChange);
  }, [encounterId, computeState]);

  if (!encounterId || !state) return null;

  const cfg = STATES[state] || STATES.yellow;
  const isGreen = state === 'green';
  const isAmber = state === 'amber';
  const signedAtLabel = entry?.signed_at ? ` · Signed ${_formatTimeAgo(entry.signed_at)}` : '';
  const copiedAtLabel = entry?.copied_at ? ` · Copied ${_formatTimeAgo(entry.copied_at)}` : '';

  const handleQuickSign = () => {
    if (isGreen) {
      // un-sign
      setEncounterStatus(encounterId, entry?.copied_at ? 'amber' : 'yellow');
      if (onSignedToggle) onSignedToggle(false);
    } else {
      setEncounterStatus(encounterId, 'green');
      if (onSignedToggle) onSignedToggle(true);
      try {
        window.dispatchEvent(new CustomEvent('writeright:note-submitted', { detail: { encounter_id: encounterId } }));
      } catch { /* ignore */ }
    }
  };

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 transition-colors ${cfg.pillClass}`}
      title={`${cfg.desc}${signedAtLabel}${copiedAtLabel}`}
      data-testid={`stoplight-header-indicator-${state}`}
    >
      <Circle className={`w-2 h-2 fill-current shrink-0 ${cfg.textClass}`} />
      <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">{cfg.label}</span>
      {/* Quick-sign / un-sign action — only show after amber (post-copy) so the
          provider doesn't accidentally sign a still-being-drafted note. */}
      {(isAmber || isGreen) && (
        <button
          type="button"
          onClick={handleQuickSign}
          className={`ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider transition-colors ${
            isGreen
              ? 'bg-emerald-500/30 text-emerald-100 hover:bg-emerald-500/40'
              : 'bg-amber-500/25 text-amber-100 hover:bg-amber-500/35'
          }`}
          title={isGreen ? 'Mark as not yet signed' : 'Mark Signed in Genesis (one-tap from header)'}
          data-testid={`stoplight-header-action-${isGreen ? 'unsign' : 'sign'}`}
        >
          {isGreen ? <><Check className="w-2.5 h-2.5" /> Signed</> : <>Sign?</>}
        </button>
      )}
    </div>
  );
};

export default EncounterStoplight;
